import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import StoreDetails from "./pages/StoreDetails";
import Stores from "./pages/Stores";
import Profile from "./pages/Profile";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import EULA from "./pages/EULA";
import ComponentShowcase from "./pages/test/ComponentShowcase";
import ColorSystem from "./pages/test/ColorSystem";
import ChartShowcase from "./pages/test/ChartShowcase";

const queryClient = new QueryClient();

const App = () => {
  const { i18n } = useTranslation();
  const [isReady, setIsReady] = useState(i18n.isInitialized);

  useEffect(() => {
    if (isReady) {
      return;
    }

    const handleInitialized = () => {
      setIsReady(true);
    };

    i18n.on('initialized', handleInitialized);

    return () => {
      i18n.off('initialized', handleInitialized);
    };
  }, [i18n, isReady]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    const applyDocumentLanguage = (lng: string) => {
      document.documentElement.lang = lng;
    };

    applyDocumentLanguage(i18n.language);

    const handleLanguageChange = (lng: string) => {
      applyDocumentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, isReady]);

  if (!isReady) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/stores" element={<Stores />} />
            <Route path="/stores/:storeId" element={<StoreDetails />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/eula" element={<EULA />} />
            {/* Test pages for visual regression testing */}
            <Route path="/test/components" element={<ComponentShowcase />} />
            <Route path="/test/colors" element={<ColorSystem />} />
            <Route path="/test/charts" element={<ChartShowcase />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
